/**
 * The animation taxonomy, the layer masks, and the blend defaults.
 *
 * **Every clip the pipeline ships is a whole-body clip.** A sword swing plants its
 * feet, a death falls with its legs, a locomotion cycle swings its arms -- all 51
 * driven joints are in the GLB, whatever family the clip belongs to. Masking is not
 * a property of a clip and never was: it is something a *caller* does at bind time,
 * by rebuilding the clip's `AnimationGroup` filtered to one of the masks below.
 *
 * That distinction was blurred once, and it cost the sword set its legs: combat clips
 * were bound to the upper mask and nothing else, so the lower half of every swing was
 * discarded before anyone could see it. The rule now is the other way round -- a clip
 * plays whole unless something explicitly composes it -- and composition is authored
 * in the Actions panel, where a torso clip is put over a chosen pair of legs.
 *
 * `layer` therefore survives as *classification*, not instruction: it records which
 * half of the body a clip was authored to carry when it is composed, which is what
 * makes "offer me clips that make sense on the torso" answerable. Nothing binds by it.
 */
import { canonicalBonesInRegion } from './bones.ts';
import type { Gender, StanceClass } from './definition.ts';

/** How the editor organises animations, and how the client bundles them. */
export const ANIMATION_FAMILIES = ['movement', 'combat', 'idle', 'social'] as const;
export type AnimationFamily = (typeof ANIMATION_FAMILIES)[number];

/**
 * The four ways a clip can be bound to a rig.
 *
 * `full` is the default everywhere: the clip drives the whole body. `lower` and
 * `upper` are the masked bindings a composition uses. `additive` is the one that is
 * genuinely a property of the clip rather than of the caller -- an additive is
 * differenced against its own first frame and is an offset, not a pose, so it can
 * never be played whole.
 */
export const ANIMATION_LAYERS = ['lower', 'upper', 'full', 'additive'] as const;
export type AnimationLayer = (typeof ANIMATION_LAYERS)[number];

/** True for clips that are offsets rather than poses -- see `ANIMATION_LAYERS`. */
export function isAdditiveClip(layer: AnimationLayer): boolean {
  return layer === 'additive';
}

/**
 * Stable clip identity, `family/group/name`, e.g. `movement/run/fwd_masc` or
 * `combat/attack/light_combo_01a`.
 *
 * Deliberately decoupled from the source path: the art ships two different naming
 * conventions for the same thing inside the bow set alone (`_Lng_` on 288 clips,
 * `_Prop_Longbow_` on the 24 kneeling ones), and the client should never inherit
 * that. The pipeline maps source paths onto these ids once.
 */
export type ClipId = string;

export function clipId(family: AnimationFamily, group: string, name: string): ClipId {
  return `${family}/${group}/${name}`;
}

/**
 * Source folders under `art-source/Animations/Synty/`, per family. The pipeline
 * walks these in step 3.7; nothing at runtime reads them.
 */
export const ANIMATION_SOURCES: Readonly<Record<AnimationFamily, readonly string[]>> = {
  movement: ['BaseLocomotion/Masculine', 'BaseLocomotion/Feminine', 'BaseLocomotion/Neutral'],
  combat: ['SwordCombat', 'BowCombat/Masculine', 'BowCombat/Feminine', 'BowCombat/Neutral'],
  idle: ['Idles/Masculine', 'Idles/Feminine'],
  social: ['EmotesAndTaunts/Masculine', 'EmotesAndTaunts/Feminine'],
};

/**
 * Clips a stance's combat vocabulary draws from. A stance is an animation bundle, a
 * weapon loadout and nothing else -- notably not a constraint on appearance.
 */
export const STANCE_COMBAT_SOURCES: Readonly<Record<StanceClass, readonly string[]>> = {
  swordsman: ['SwordCombat'],
  archer: ['BowCombat/Masculine', 'BowCombat/Feminine', 'BowCombat/Neutral'],
};

/**
 * The upper/lower split, in canonical (rig C) bone names.
 *
 * Derived from `BONE_MAP` rather than transcribed, so the masks and the retarget map
 * cannot drift apart. `Root` is in neither: the server owns position and every
 * shipped clip is used in its in-place variant.
 *
 * **The seam sits above `spine_01`**, settled by the step 2.7 tuning pass rather than
 * assumed. `Pelvis` in the lower layer was the starting point and is necessary but not
 * sufficient: a run pitches the hips forward and a sword guard pitches the spine
 * forward, and with the whole spine on the upper layer the two *compound* into a
 * pronounced hunch. Letting the lowest spine joint follow the locomotion absorbs the
 * run's pitch, so the combat pose sits on top of a correctly-pitched base instead of
 * adding to it.
 *
 * The seam is data, not code -- it is the `region` field in `rigMap.json` -- so moving
 * it again costs a one-line edit and no rebuild. It only ever applies to a
 * *composition*: a clip played whole ignores both masks.
 */
export const BONE_MASKS: Readonly<Record<'lower' | 'upper', readonly string[]>> = {
  lower: canonicalBonesInRegion('lower'),
  upper: canonicalBonesInRegion('upper'),
};

/**
 * Seam and crossfade defaults, from the step 2.7 tuning pass.
 *
 * `upperWeight: 1` -- the torso clip owns the torso outright while a composition is
 * asserted. Dialling it below 1 blends the legs clip's own arm swing back in, which
 * blurs the weapon-ready read; the compounding-pitch problem it might have addressed
 * is better solved at the seam itself (see `BONE_MASKS`).
 *
 * `blendMs: 150` -- about a fifth of the 0.73 s run cycle, short enough that a
 * direction change feels responsive and long enough that the crossfade is not a cut.
 *
 * Both are judgement, not measurement: this is the one piece of work in the project
 * that cannot be automated. What *is* verified is that the crossfade conserves the
 * torso -- the legs clip and the torso clip always sum to exactly one full pose, so
 * the spine is never driven by a partial blend.
 */
export const LAYER_BLEND: { upperWeight: number; blendMs: number } = {
  upperWeight: 1,
  blendMs: 150,
};

/** Gendered clip families ship masculine and feminine cuts; others are neutral. */
export type ClipGender = Gender | 'neutral';

/** Empty when the layer masks are well-formed. Run by `pipeline check`. */
export function verifyBoneMasks(): string[] {
  const problems: string[] = [];

  for (const layer of ['lower', 'upper'] as const) {
    if (BONE_MASKS[layer].length === 0) problems.push(`${layer} mask is empty`);
  }

  const lower = new Set(BONE_MASKS.lower);
  const overlap = BONE_MASKS.upper.filter((bone) => lower.has(bone));
  if (overlap.length > 0) {
    problems.push(`bones claimed by both layers: ${overlap.join(', ')}`);
  }

  if (BONE_MASKS.lower.includes('Root') || BONE_MASKS.upper.includes('Root')) {
    problems.push('Root must be driven by neither layer -- the server owns position');
  }

  return problems;
}
